import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  useBreakpointValue
} from '@chakra-ui/react';
import { isValidEmail, isValidUSPhone } from "../../../../libraries/utils";
import { useAuth } from "../../../../auth-context/auth.context";
import CreatableSelect from "react-select/creatable";
import { getFirmLocationsByFirmId } from "../../../../api/user-service";

interface UserAddModalProps {
  isLoading: boolean,
  isOpen: boolean,
  onClose: () => void,
  onSave: (e: {
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber?: string,
    role: 'manager' | 'supervisor' | 'user',
    firmLocations: { value: string, label: string, source: 'api' | 'new' }[],
    note: string,
  }) => Promise<void>
}

const AddAdvisorModal: React.FC<UserAddModalProps> = ({ isLoading, isOpen, onClose, onSave }) => {
  const { user } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [note, setNote] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [role, setRole] = useState<'manager' | 'supervisor' | 'user'>('user');
  const [firmLocations, setFirmLocations] = useState<any>([]);
  const [firmLocationOptions, setFirmLocationOptions] = useState<any>([]);
  const [isFirmLocationsLoading, setIsFirmLocationsLoading] = useState(false);

  const DEFAULT_FIRM = {
    label: `${user?.firm?.name || user?.fullName}`,
    value: user?.firmId,
    source: "default",
  };

  const save = async () => {
    let message = null;

    if (!firstName) {
      message = 'First name is required.';
    } else if (!lastName) {
      message = 'Last name is required.';
    } else if (!email) {
      message = 'Email is required.';
    } else if (!isValidEmail(email)) {
      message = 'Invalid email address.';
    } else if (phoneNumber && !isValidUSPhone(phoneNumber)) {
      message = 'Please input valid phone number.';
    } else if (role === 'manager' && !firmLocations) {
      message = 'Firm Location is required.';
    }

    if (message) {
      setErrorMessage(message);
    } else {
      const phoneNumberDigits = phoneNumber.replace(/\D/g, '');
      await onSave({
        email: email.trim().toLowerCase(),
        firstName,
        lastName,
        ...(phoneNumber ? {
          phoneNumber: `+1${phoneNumberDigits}`
        } : {}),
        role,
        firmLocations,
        note,
      });
      handleClose();
    }
  }

  // Fetch firm locations from an API.
  const loadFirmLocationOptions = async () => {
    try {
      setIsFirmLocationsLoading(true);
      const { firmLocations } = await getFirmLocationsByFirmId(user.firmId);
      if (firmLocations?.length > 0) {
        const options = firmLocations.map((location: any) => ({
          label: location.name,
          value: location.firmId, // Firm ID from backend
          source: "api",
        }));

        setFirmLocationOptions(options);
      }
    } catch (error) {
      console.error("Failed to fetch firm locations", error);
    } finally {
      setIsFirmLocationsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && ['admin', 'manager'].includes(user?.firmRole)) {
      loadFirmLocationOptions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (['supervisor', 'user'].includes(role) && user?.firmRole === 'admin') {
      setFirmLocations([DEFAULT_FIRM]);
    } else {
      setFirmLocations([]);
      setFirmLocationOptions((prev: any) => prev.filter((option: any) => option.source === 'api'));
    }
  }, [role]);

  const handleAddNewLocation = (inputValue: string) => {
    // Check if the location already exists.
    const existingOption = firmLocationOptions.find(
      (option: any) => option.label.toLowerCase() === inputValue.toLowerCase()
    );
    if (!existingOption) {
      // Add the new location to the options and select it.
      const newLocation = { label: inputValue, value: inputValue, source: "new", };
      setFirmLocationOptions((prev: any) => [...prev, newLocation]);
      setFirmLocations((prev: any) => [...prev, newLocation]); // Add to selected locations.
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    let onlyNums = value.replace(/\D/g, '');
    let formattedNumber;
    if (onlyNums.length <= 3) {
      formattedNumber = onlyNums;
    } else if (onlyNums.length <= 6) {
      formattedNumber = `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3)}`;
    } else {
      formattedNumber = `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
    }

    setPhoneNumber(formattedNumber);
    setErrorMessage(null);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setErrorMessage(null);
  }

  const handleClose = () => {
    onClose();
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhoneNumber('');
    setFirmLocations([]);
    setFirmLocationOptions([]);
    setErrorMessage(null);
  }

  return (
    <Modal isOpen={isOpen} onClose={() => handleClose()} size="xl">
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>Add User to {user?.fullName}</ModalHeader>
        <ModalCloseButton/>
        <ModalBody>
          <Grid
            templateColumns={useBreakpointValue({ base: '1fr', md: 'repeat(2, 1fr)' })}
            gap={useBreakpointValue({ base: 4, md: 2 })}
          >
            <FormControl isRequired>
              <FormLabel>First Name</FormLabel>
              <Input placeholder='John' value={firstName}
                     onChange={(e) => {
                       setFirstName(e.target.value);
                       setErrorMessage(null);
                     }}/>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Last Name</FormLabel>
              <Input placeholder='Doe' value={lastName}
                     onChange={(e) => {
                       setLastName(e.target.value);
                       setErrorMessage(null);
                     }}/>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Email</FormLabel>
              <Input placeholder='you@example.com' type="email" colorScheme="brandScheme" value={email}
                     onChange={(e) => handleEmailChange(e.target.value)}/>
            </FormControl>
            <FormControl>
              <FormLabel>Phone Number (optional)</FormLabel>
              <Input colorScheme="brandScheme" value={phoneNumber}
                     type="tel"
                     placeholder="(201)-555-0123"
                     onChange={(e) => handlePhoneNumberChange(e)}/>
            </FormControl>
            <GridItem colSpan={2}>
              <FormLabel mt={2}>Firm Location</FormLabel>
              <Text color='gray.500' mb={2}>
                Select existing locations or add new ones by typing.
              </Text>
              <CreatableSelect
                isLoading={isFirmLocationsLoading}
                isMulti
                value={firmLocations} // Controlled input for selected locations.
                placeholder="Select or add firm locations"
                onChange={setFirmLocations}
                options={firmLocationOptions} // Options fetched from the backend.
                isClearable
                onCreateOption={
                  user?.firmRole === "admin" ? handleAddNewLocation : undefined // Only allow creating options for admins
                }
                formatCreateLabel={(inputValue) =>
                  user?.firmRole === "admin"
                    ? `Add "${inputValue}"` // Show 'Add' label only for admins
                    : null
                }
              />
            </GridItem>
            <GridItem colSpan={2}>
              <FormLabel mt={2}>Role</FormLabel>
              <RadioGroup value={role}
                          onChange={(e: 'manager' | 'supervisor' | 'user') => setRole(e)}>
                <HStack>
                  {
                    user?.firmRole === 'admin' &&
                    <Radio colorScheme="brandScheme" fontSize='sm'
                           value='manager'>Manager</Radio>
                  }
                  <Radio colorScheme="brandScheme" fontSize='sm'
                         value='supervisor'>Supervisor</Radio>
                  <Radio colorScheme="brandScheme" fontSize='sm'
                         value='user'>User</Radio>
                </HStack>
              </RadioGroup>
              <Text color='gray.500'>
                {role === "manager"
                  ? "Manager can add/delete users and see all prospects of firm locations."
                  : role === "supervisor"
                    ? "Supervisor can see all prospects of the firm location."
                    : "User can only see prospects invited by them."}
              </Text>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel>Note</FormLabel>
                <Textarea placeholder='Internal note about advisor' value={note}
                          rows={3}
                          onChange={(e) => {
                            setNote(e.target.value);
                          }}/>
              </FormControl>
            </GridItem>
          </Grid>
          <FormControl>
            {errorMessage ? (
              <FormHelperText color='red'>
                {errorMessage}
              </FormHelperText>
            ) : null}
          </FormControl>
          <Text fontSize="md" mt="8px" color='blue.500'>Your invitation will expire in 90 days.</Text>
        </ModalBody>
        <ModalFooter>
          <Button isDisabled={isLoading} isLoading={isLoading} colorScheme="brandScheme" mr={3}
                  onClick={() => save()}>Save</Button>
          <Button colorScheme="brandScheme" variant="ghost" onClick={() => handleClose()}>Cancel</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default AddAdvisorModal;
